import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box, Grid, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TabPanel from '@atlaskit/tabs/tab-panel';
import Tag from '@atlaskit/tag/removable-tag';

import { getMinionOutputItem } from '../get-minion-output-item';
import { messages } from '../messages';
import type { OrchestrationRound } from '../types';

import { Panel } from './panel-wrapper';

const styles = cssMap({
	minionGrid: {
		gridTemplateColumns: '180px 100px 1fr',
	},
});

type OrchestrationOverviewPanelProps = {
	orchestrationRounds: OrchestrationRound[];
};

export const OrchestrationOverviewPanel: ({
	orchestrationRounds,
}: OrchestrationOverviewPanelProps) => React.JSX.Element = ({
	orchestrationRounds,
}: OrchestrationOverviewPanelProps) => {
	const { formatMessage } = useIntl();

	if (orchestrationRounds.length === 0) {
		return (
			<TabPanel>
				<Panel testId="sain-online-debugger-orchestration-overview-panel">
					<Text>{formatMessage(messages.ai_debugger_modal_no_orchestration_data)}</Text>
				</Panel>
			</TabPanel>
		);
	}

	return (
		<TabPanel>
			<Panel testId="sain-online-debugger-orchestration-overview-panel">
				<Stack space="space.200">
					{orchestrationRounds.map((round, roundIndex) => (
						<Box key={roundIndex}>
							<Stack space="space.100">
								{/* Loop Header */}
								<Inline space="space.100" alignBlock="center">
									<Heading size="small">
										{formatMessage(messages.ai_debugger_modal_orchestration_loop_heading, {
											number: roundIndex,
										})}
									</Heading>
									<Tag
										text={formatMessage(messages.ai_debugger_modal_orchestration_minions_count, {
											count: round.length,
										})}
										isRemovable={false}
										migration_fallback="lozenge"
									/>
								</Inline>
								{/* Column Headers */}
								<Grid xcss={styles.minionGrid} gap="space.100" alignItems="center">
									<Heading size="xsmall">
										{formatMessage(messages.ai_debugger_modal_minion_name_label)}
									</Heading>
									<Heading size="xsmall">
										{formatMessage(messages.ai_debugger_modal_execution_duration_label)}
									</Heading>
									<Heading size="xsmall">
										{formatMessage(messages.ai_debugger_modal_minion_input_label)}
									</Heading>
								</Grid>
								{/* Minions Grid */}
								<Box>
									{round.map((minion, minionIndex) => {
										const minionInfo = getMinionOutputItem(minion);
										const minionId = minionInfo.minion_id;
										const minionInput = minionInfo.minion_input;
										const executionDuration = minionInfo.minion_execution_duration;

										return (
											<Grid
												key={`${minionId}-${minionIndex}`}
												xcss={styles.minionGrid}
												gap="space.100"
												alignItems="center"
											>
												{minionId ? (
													<Text color="color.text.subtle">{minionId}</Text>
												) : (
													<Text color="color.text.subtlest" testId="disabled-minion-id">
														{formatMessage(messages.ai_debugger_modal_disabled)}
													</Text>
												)}

												{executionDuration !== undefined ? (
													<Text color="color.text.subtle">
														{formatMessage(messages.ai_debugger_modal_execution_duration, {
															duration: executionDuration,
														})}
													</Text>
												) : (
													<Text color="color.text.subtlest" testId="disabled-execution-duration">
														{formatMessage(messages.ai_debugger_modal_disabled)}
													</Text>
												)}

												{minionInput ? (
													<Text color="color.text.subtle">
														{typeof minionInput === 'object'
															? JSON.stringify(minionInput, null, 2)
															: minionInput}
													</Text>
												) : (
													<Text color="color.text.subtlest" testId="disabled-minion-input">
														{formatMessage(messages.ai_debugger_modal_disabled)}
													</Text>
												)}
											</Grid>
										);
									})}
								</Box>
							</Stack>
						</Box>
					))}
				</Stack>
			</Panel>
		</TabPanel>
	);
};
